import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, ClipboardList } from 'lucide-react';
import { useT } from '@/contexts/I18nContext';
import { cn } from '@/lib/utils';
import { Tooltip } from '@/components/ui/tooltip';

// The zone card's problem area: ONE problem at a time, worst first, paged with
// chevrons / arrow keys. Photo slides letterbox (never crop) on a quiet padded
// background; non-photo slides (overdue tasks) use calm typography — no alert
// fill. The parent orders the slides and owns the lightbox: a photo tap
// reports its index into the parent's lightbox photo list.

export interface TaskTile {
  key: string;
  name: string;
  /** Task-status dot colour (TASK_DOT family — matches the tasks page). */
  dotClass: string;
  /** Due HH:MM ('' when the task has no due time). */
  time: string;
}

export type ProblemSlide =
  | {
      kind: 'photo';
      key: string;
      imageUrl: string;
      /** name · status · HH:MM — same string as the lightbox caption. */
      caption: string;
      dotClass: string;
      /** Index into the parent's lightbox photo list. */
      photoIndex: number;
    }
  | {
      kind: 'tasks';
      key: string;
      overdue: number;
      pending: number;
      /** Per-task tile rows, overdue first (new BE). Absent on old BE →
       *  counts-only fallback rendering. */
      items?: TaskTile[];
    };

// The letterbox is aspect-video, so the tile list gets one extra text line at
// most: >MAX rows folds the overflow into a «+N ещё» tail row.
const MAX_TASK_ROWS = 5;

function TaskTileList({ items }: { items: TaskTile[] }) {
  const { t } = useT();
  const shown = items.length > MAX_TASK_ROWS ? items.slice(0, MAX_TASK_ROWS - 1) : items;
  const rest = items.length - shown.length;
  return (
    <div className="flex size-full flex-col justify-center gap-1 overflow-hidden px-3 py-2">
      <span className="mb-0.5 flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
        <ClipboardList className="size-3.5" aria-hidden />
        {t('portfolio.rowTasks')}
      </span>
      {shown.map(it => (
        <div key={it.key} className="flex items-center gap-1.5 text-xs leading-tight">
          <span className={cn('size-2 shrink-0 rounded-full', it.dotClass)} aria-hidden />
          <span className="min-w-0 flex-1 truncate text-left">{it.name}</span>
          {it.time && (
            <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
              {it.time}
            </span>
          )}
        </div>
      ))}
      {rest > 0 && (
        <span className="pl-3.5 text-left text-[11px] text-muted-foreground">
          {t('portfolio.moreTasks', { n: rest })}
        </span>
      )}
    </div>
  );
}

export function ProblemCarousel({
  slides,
  onOpenPhoto,
  className,
}: {
  slides: ProblemSlide[];
  onOpenPhoto: (photoIndex: number) => void;
  className?: string;
}) {
  const { t } = useT();
  const [rawIndex, setRawIndex] = useState(0);
  // The dashboard refetches every few seconds and slides can drop out — clamp
  // instead of resetting so paging survives a refresh.
  const index = Math.max(0, Math.min(rawIndex, slides.length - 1));
  if (slides.length === 0) return null;
  const slide = slides[index];
  const prev = () => setRawIndex(Math.max(0, index - 1));
  const next = () => setRawIndex(Math.min(slides.length - 1, index + 1));

  return (
    <div
      role="group"
      aria-label={t('portfolio.problemsCarousel')}
      tabIndex={0}
      className={cn(
        'min-w-0 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        className,
      )}
      onKeyDown={e => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        // The card behind is a giant button — arrows must never leak into it.
        e.preventDefault();
        e.stopPropagation();
        if (e.key === 'ArrowLeft') prev();
        else next();
      }}
    >
      <div className="relative aspect-video overflow-hidden rounded-md border border-border bg-muted/40">
        {slide.kind === 'photo' ? (
          <Tooltip content={slide.caption}><button
            type="button"
            aria-label={slide.caption}
            className="group relative block size-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
            onClick={e => {
              e.stopPropagation();
              onOpenPhoto(slide.photoIndex);
            }}
          >
            <img
              src={slide.imageUrl}
              alt={slide.caption}
              draggable={false}
              loading="lazy"
              className="absolute inset-0 size-full object-contain p-1"
            />
            <span
              className={cn(
                'absolute left-1.5 top-1.5 size-2 rounded-full ring-1 ring-black/20',
                slide.dotClass,
              )}
              aria-hidden
            />
            <span className="pointer-events-none absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/60 to-transparent px-2 pb-1.5 pt-5 text-left text-[11px] font-medium text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
              {slide.caption}
            </span>
          </button></Tooltip>
        ) : slide.items ? (
          <TaskTileList items={slide.items} />
        ) : (
          // Old-BE fallback: no per-task items → counts summary.
          <div className="flex size-full flex-col items-center justify-center gap-0.5 px-3 text-center">
            <ClipboardList className="mb-1 size-5 text-amber-500" aria-hidden />
            <span className="text-xs text-muted-foreground">{t('portfolio.rowTasks')}</span>
            {slide.overdue > 0 && (
              <span className="text-sm font-medium">
                {t('portfolio.overdue', { n: slide.overdue })}
              </span>
            )}
            {slide.pending > 0 && (
              <span
                className={cn(
                  slide.overdue > 0 ? 'text-xs text-muted-foreground' : 'text-sm font-medium',
                )}
              >
                {t('portfolio.pending', { n: slide.pending })}
              </span>
            )}
          </div>
        )}

        {slides.length > 1 && index > 0 && (
          <button
            type="button"
            aria-label={t('portfolio.prevSlide')}
            className="absolute left-1 top-1/2 -translate-y-1/2 rounded-full border border-border bg-background/80 p-1 shadow-sm backdrop-blur-sm hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={e => {
              e.stopPropagation();
              prev();
            }}
          >
            <ChevronLeft className="size-3.5" />
          </button>
        )}
        {slides.length > 1 && index < slides.length - 1 && (
          <button
            type="button"
            aria-label={t('portfolio.nextSlide')}
            className="absolute right-1 top-1/2 -translate-y-1/2 rounded-full border border-border bg-background/80 p-1 shadow-sm backdrop-blur-sm hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={e => {
              e.stopPropagation();
              next();
            }}
          >
            <ChevronRight className="size-3.5" />
          </button>
        )}
      </div>

      {slides.length > 1 && (
        <div className="mt-1.5 flex items-center justify-center gap-1" aria-hidden>
          {slides.map((s, i) => (
            <span
              key={s.key}
              className={cn(
                'size-1.5 rounded-full transition-colors',
                i === index ? 'bg-foreground/70' : 'bg-muted-foreground/30',
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
}
